import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import AccountMenu from 'components/account/AccountMenu';
import { LoginSection } from 'components/landing-pages-v2/shared/header/LoginSection';
import { useObservability } from 'helpers/observability';
import { usePrefetchNavTransactions } from 'helpers/useNavTransactions';

import { GlobalNavSVG } from './GlobalNavSVG';
import { useLargerThanSM } from './useLargerThanSM';

interface AccountSectionProps {
  isLoggedIn: boolean;
  isAuthenticating?: boolean;
  showLogin: boolean;
  initials: string[];
  redirectToLogin: () => void;
  trackAccountClick: () => void;
  trackMenuClick: () => void;
  onMobileMenuToggle: () => void;
  isMobileMenuVisible: boolean;
  hamburgerRef?: React.RefObject<HTMLButtonElement>;
  // When true, the menu/dots/sign-in render in the inverse (light) foreground for
  // use over a dark/transparent header; otherwise they use the primary foreground.
  inverse?: boolean;
}

export const AccountSection: React.FC<AccountSectionProps> = ({
  isLoggedIn,
  isAuthenticating = false,
  showLogin,
  initials,
  redirectToLogin,
  trackAccountClick,
  trackMenuClick,
  onMobileMenuToggle,
  isMobileMenuVisible,
  hamburgerRef,
  inverse,
}) => {
  const foregroundClass = inverse ? 'text-od-content-inverse-primary' : 'text-od-content-primary';
  const { trackJourneyEvent } = useObservability();
  const largerThanSM = useLargerThanSM();
  const [isExpanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Warm the shared nav-transactions store from the always-mounted header so the
  // mobile menu (whose list only mounts on open) is populated instantly.
  usePrefetchNavTransactions();

  const toggleDropdown = useCallback(() => setExpanded((prev) => !prev), []);
  const closeDropdown = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, closeDropdown]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    },
    [closeDropdown],
  );

  useEffect(() => {
    if (!isExpanded) return;

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded, handleEscape]);

  const handleAvatarPress = useCallback(() => {
    trackAccountClick();
    trackMenuClick();
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'user-account-menu-click' });
    toggleDropdown();
  }, [trackAccountClick, trackMenuClick, trackJourneyEvent, toggleDropdown]);

  const handleLoginPress = useCallback(() => {
    trackAccountClick();
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'account-section-click' });
    redirectToLogin();
  }, [trackAccountClick, trackJourneyEvent, redirectToLogin]);

  const handleDotsPress = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      trackMenuClick();
      toggleDropdown();
    },
    [trackMenuClick, toggleDropdown],
  );

  const handleMobileMenuPress = useCallback(() => {
    trackMenuClick();
    onMobileMenuToggle();
  }, [trackMenuClick, onMobileMenuToggle]);

  const hamburger = (
    <Box className="md:hidden">
      <Box
        ref={hamburgerRef}
        onClick={handleMobileMenuPress}
        aria-label="Navigation menu"
        aria-haspopup="true"
        aria-expanded={isMobileMenuVisible}
        analyticsName="cosmos-landing-page-header-menu-button"
        className={cn(
          'flex cursor-pointer items-center justify-center focus:outline-none',
          foregroundClass,
        )}
      >
        <GlobalNavSVG isOpen={isMobileMenuVisible} />
      </Box>
    </Box>
  );

  return (
    <AccountSectionFrame containerRef={containerRef}>
      {isAuthenticating && (
        <>
          <Box
            aria-hidden
            className="skeleton hidden h-8 w-8 shrink-0 rounded-full bg-od-bg-tertiary md:block"
          />
          {hamburger}
        </>
      )}
      {!isAuthenticating && isLoggedIn && (
        <>
          <Box
            onClick={handleAvatarPress}
            analyticsName="account-section-click"
            aria-label="User account menu"
            aria-haspopup="true"
            aria-expanded={isExpanded}
            className="cursor-pointer border-0 bg-transparent p-0 max-md:hidden"
          >
            <Box className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-od-bg-inverse-primary">
              <Text as="span" className="text-label-sm font-medium text-od-content-inverse-primary">
                {initials}
              </Text>
            </Box>
          </Box>
          {hamburger}
        </>
      )}
      {!isAuthenticating && !isLoggedIn && (
        <Row className="items-center gap-1">
          <LoginSection showLogin={showLogin} onPress={handleLoginPress} inverse={inverse} />
          <Box className="max-md:hidden">
            <Box
              onClick={handleDotsPress}
              aria-label="More options"
              aria-haspopup="true"
              aria-expanded={isExpanded}
              analyticsName="cosmos-landing-page-header-menu-button"
              className={cn(
                'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full hover:bg-od-bg-hover-primary',
                foregroundClass,
              )}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <circle cx="4" cy="10" r="1.75" />
                <circle cx="10" cy="10" r="1.75" />
                <circle cx="16" cy="10" r="1.75" />
              </svg>
            </Box>
          </Box>
          {hamburger}
        </Row>
      )}
      {largerThanSM && <AccountMenu isExpanded={isExpanded} isLoggedIn={isLoggedIn} />}
    </AccountSectionFrame>
  );
};

const AccountSectionFrame: React.FC<
  React.PropsWithChildren<{ containerRef: React.RefObject<HTMLDivElement> }>
> = ({ children, containerRef }) => (
  <Box
    as="div"
    ref={containerRef}
    className="relative flex h-10 w-[128px] items-center md:w-[156px]"
  >
    <Box className="absolute inset-0 flex items-center justify-end gap-1">{children}</Box>
  </Box>
);
